<template lang="pug">
#publications-page
  section-hero-alt(:title='t("pages.publications.title")')
  section.publications-section
    base-page-content
      .publications-shell
        .publications-intro
          p.publications-kicker {{ t('pages.publications.kicker') }}
          h2.publications-heading {{ t('pages.publications.heading') }}
          p.publications-copy {{ t('pages.publications.intro') }}
          p.publications-note
            strong {{ t('pages.publications.publicationCount', publications.length) }}
            a.publications-source-hint(
              href='https://ecampus.uni-koblenz.de/qisserver/a/fs.res.frontend/pub/search'
              target='_blank'
              rel='noopener noreferrer'
              :aria-label='t("pages.publications.sourceHint")'
            )
              span.publications-source-hint__icon i
              span.publications-source-hint__tooltip
                | {{ t('pages.publications.sourceHint') }}
        .publications-toolbar
          v-text-field.publications-search(
            v-model='searchQuery'
            :label='t("pages.publications.search")'
            prepend-inner-icon='mdi-magnify'
            variant='outlined'
            density='comfortable'
            hide-details
          )
          ClientOnly
            v-select.publications-year(
              v-model='selectedYear'
              :items='yearOptions'
              item-title='label'
              item-value='value'
              :label='t("pages.publications.year")'
              variant='outlined'
              density='comfortable'
              hide-details
            )
            template(#fallback)
              .publications-year-fallback
                span.publications-year-label {{ t('pages.publications.year') }}
                strong {{ selectedYearLabel }}
        .year-sections(v-if='yearSections.length')
          section.year-section(
            v-for='section in yearSections'
            :id='`year-${section.year}`'
            :key='section.year'
          )
            .year-heading
              h2 {{ section.year }}
              span {{ t('pages.publications.publicationCount', section.publications.length) }}
            .publication-grid
              article.publication-card(
                v-for='publication in section.publications'
                :key='publication.id'
              )
                .publication-main
                  p.publication-type {{ publication.type }}
                  p.publication-authors {{ publication.authors }}
                  h3.publication-title
                    nuxt-link(:to='localePath(publication.detailPath)') {{ publication.title }}
                  .publication-context
                    p.publication-venue(v-if='publicationVenueLabel(publication)') {{ publicationVenueLabel(publication) }}
                    publication-actions(:publication='publication', show-details)
        .empty-state(v-else)
          h3 {{ t('pages.publications.emptyTitle') }}
          p {{ t('pages.publications.emptyBody') }}
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from 'nuxt/app'
import type { PublicationEntry } from '~/services/publicationsService'
import { getAllPublications } from '~/services/publicationsService'
import { createPageSchemaContext, createSeoMeta } from '~/utils/schema'

defineOptions({
  name: 'PublicationsPage',
})

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { t } = useI18n()
const localePath = useLocalePath()
const { pageUrl } = createPageSchemaContext(
  route.path,
  runtimeConfig.public.baseUrl,
)
const description = t('seo.publicationsDescription')

useSeoMeta(createSeoMeta(t('pages.publications.title'), description, pageUrl))
const { data: publicationsData } = await useAsyncData('publications', () =>
  getAllPublications(),
)

const searchQuery = ref('')
const selectedYear = ref('all')
const publications = computed(() => publicationsData.value ?? [])

const availableYears = computed(() => {
  return Array.from(
    new Set(publications.value.map((publication) => publication.year)),
  )
})

const yearOptions = computed(() => {
  return [
    { label: t('pages.publications.allYears'), value: 'all' },
    ...availableYears.value.map((year) => ({
      label: year,
      value: year,
    })),
  ]
})

const selectedYearLabel = computed(() => {
  return (
    yearOptions.value.find((option) => option.value === selectedYear.value)
      ?.label ?? t('pages.publications.allYears')
  )
})

const filteredPublications = computed(() => {
  const keyword = searchQuery.value.trim().toLowerCase()

  return publications.value.filter((publication: PublicationEntry) => {
    const matchesYear =
      selectedYear.value === 'all' || publication.year === selectedYear.value

    if (!matchesYear) {
      return false
    }

    if (!keyword) {
      return true
    }

    return [
      publication.title,
      publication.authors,
      publication.type,
      publication.venue ?? '',
      publication.publisher ?? '',
      publication.abstract ?? '',
    ].some((value) => value.toLowerCase().includes(keyword))
  })
})

const yearSections = computed(() => {
  return availableYears.value
    .map((year) => ({
      year,
      publications: filteredPublications.value.filter(
        (publication) => publication.year === year,
      ),
    }))
    .filter((section) => section.publications.length > 0)
})

function publicationVenueLabel(publication: PublicationEntry) {
  if (publication.venue) {
    return t('pages.publications.inVenue', { venue: publication.venue })
  }

  if (publication.publisher) {
    return t('pages.publications.publishedBy', {
      publisher: publication.publisher,
    })
  }

  return ''
}
</script>

<style scoped>
.publications-section {
  padding: 4.5rem 0 5rem;
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface-muted) 92%, var(--surface-page)) 0%,
      var(--surface-page) 16rem
    ),
    radial-gradient(
      circle at top left,
      rgba(0, 192, 216, 0.08),
      transparent 30rem
    );
}

.publications-shell {
  display: grid;
  gap: 1rem;
}

.publications-intro {
  padding: 1.5rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.publications-kicker {
  margin: 0 0 0.75rem;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.76rem;
  font-weight: 700;
}

.publications-heading {
  margin: 0;
  font-size: clamp(1.7rem, 2.8vw, 2.5rem);
  line-height: 0.98;
}

.publications-copy {
  max-width: 52rem;
  margin: 1rem 0 0;
  color: var(--color-text);
  line-height: 1.65;
}

.publications-note {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 1rem 0 0;
  color: var(--color-text-muted);
}

.publications-source-hint {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;
}

.publications-source-hint__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.3rem;
  height: 1.3rem;
  border: 1px solid var(--border-default);
  border-radius: 999px;
  background: var(--surface-muted);
  color: var(--color-text-strong);
  font-size: 0.78rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    transform 0.18s ease;
}

.publications-source-hint__tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 0.7rem);
  z-index: 1;
  width: min(18rem, 70vw);
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
  color: var(--color-text);
  box-shadow: var(--shadow-soft);
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 0.25rem);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.publications-source-hint:hover .publications-source-hint__icon,
.publications-source-hint:focus-visible .publications-source-hint__icon {
  border-color: var(--border-strong);
  background: var(--surface-soft);
  transform: translateY(-1px);
}

.publications-source-hint:hover .publications-source-hint__tooltip,
.publications-source-hint:focus-visible .publications-source-hint__tooltip {
  opacity: 1;
  transform: translate(-50%, 0);
}

.publications-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 12rem;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.publications-year-fallback {
  min-width: 12rem;
  min-height: 3.5rem;
  display: grid;
  align-content: center;
  gap: 0.2rem;
  padding: 0.55rem 0.95rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.publications-year-label {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.year-sections {
  display: grid;
  gap: 3rem;
}

.year-section {
  scroll-margin-top: 6rem;
}

.year-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;
  padding-bottom: 0.5rem;
}

.year-heading h2 {
  margin: 0;
  font-size: clamp(1.75rem, 2.5vw, 2.3rem);
  line-height: 0.95;
}

.year-heading span {
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.72rem;
  font-weight: 700;
}

.publication-grid {
  display: grid;
  gap: 0.85rem;
}

.publication-card {
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.publication-main {
  display: grid;
  gap: 0.35rem;
  padding: 0.95rem 1.05rem;
}

.publication-type {
  margin: 0;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.7rem;
  font-weight: 700;
}

.publication-authors {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.84rem;
  line-height: 1.45;
}

.publication-title {
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.25;
}

.publication-title a {
  color: var(--color-text-strong);
  text-decoration: none;
}

.publication-title a:hover {
  text-decoration: underline;
}

.publication-context {
  display: grid;
  gap: 0.6rem;
  margin-top: 0.35rem;
}

.publication-venue {
  margin: 0;
  color: var(--color-text);
  line-height: 1.45;
}

.publication-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 0.85rem;
  padding-top: 0.55rem;
  border-top: 1px solid var(--border-default);
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--brand-primary);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  transition: color 0.18s ease;
}

.action-link:hover {
  color: color-mix(in srgb, var(--brand-primary) 78%, black);
  text-decoration: underline;
}

.action-link--button {
  appearance: none;
}

.empty-state {
  padding: 3rem 1.5rem;
  border: 1px solid var(--border-subtle);
  background: var(--surface-card);
}

.empty-state h3,
.empty-state p {
  margin: 0;
}

.empty-state p {
  margin-top: 0.6rem;
  color: var(--color-text-muted);
}

@media (max-width: 760px) {
  .publications-toolbar {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .publications-note {
    align-items: flex-start;
  }

  .publications-intro,
  .publication-main {
    padding: 1rem;
  }
}
</style>
